import React from 'react';
import {
  SearchBoxCore,
  SearchBoxOptions,
  SearchBoxRetrieveResponse,
  SearchBoxSuggestion,
  SessionToken
} from '@mapbox/search-js-core';

/**
 * Type for coordinate suggestions with custom _geometry property.
 * These are generated locally from coordinate input (e.g., "40.7128, -74.0060")
 * and don't require a Mapbox retrieve() API call.
 */
export type CoordinateSuggestion = SearchBoxSuggestion & {
  _geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
};

/**
 * Type for coordinate retrieve responses (simpler than full Mapbox response).
 * Used when selecting a coordinate suggestion to provide a consistent response
 * format without calling the Mapbox API. Seems duplicated with SearchBoxFeatureProperties,
 * but actually `properties` is missing a lot of fields (name_preferred, mapbox_id, feature_type...)
 */
export interface CoordinateRetrieveResponse {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    properties: {
      name: string;
      full_address: string;
      coordinates: {
        longitude: number;
        latitude: number;
      };
    };
    geometry: {
      type: 'Point';
      coordinates: [number, number];
    };
  }>;
}

/**
 * Type guard to check if a suggestion is a coordinate suggestion.
 * Coordinate suggestions have a custom _geometry property added by
 * searchBoxCustomSearch() and don't need Mapbox retrieve() calls.
 *
 * @param suggestion - The suggestion to check
 * @returns True if suggestion is a CoordinateSuggestion
 */
export function isCoordinateSuggestion(
  suggestion: SearchBoxSuggestion
): suggestion is CoordinateSuggestion {
  return (
    '_geometry' in suggestion &&
    typeof (suggestion as Partial<CoordinateSuggestion>)._geometry ===
      'object' &&
    (suggestion as Partial<CoordinateSuggestion>)._geometry !== null &&
    Array.isArray(
      (suggestion as Partial<CoordinateSuggestion>)._geometry?.coordinates
    ) &&
    (suggestion as Partial<CoordinateSuggestion>)._geometry?.coordinates
      .length === 2
  );
}

/**
 * Creates a simplified FeatureCollection response for coordinate suggestions.
 * Coordinate suggestions (e.g., from "40.7128, -74.0060" input) don't need
 * a full Mapbox retrieve() call, so we construct a minimal response locally.
 *
 * @param suggestion - A coordinate suggestion with _geometry property
 * @returns A FeatureCollection with a single Point feature
 */
export const createCoordinateRetrieveResponse = (
  suggestion: CoordinateSuggestion
): CoordinateRetrieveResponse => ({
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {
        name: suggestion.name,
        full_address: suggestion.full_address,
        coordinates: {
          longitude: suggestion._geometry.coordinates[0],
          latitude: suggestion._geometry.coordinates[1]
        }
      },
      geometry: suggestion._geometry
    }
  ]
});

/**
 * Handles selection of a coordinate suggestion (no API retrieve needed).
 * Updates UI state, centers the map, and triggers callbacks.
 *
 * @param suggestion - The coordinate suggestion to select
 * @param params - Object containing callbacks and state setters
 */
export const handleCoordinateSelection = (
  suggestion: CoordinateSuggestion,
  params: {
    setInputValue: (value: string) => void;
    setIsOpen: (open: boolean) => void;
    onChange?: (value: string) => void;
    onRetrieve?: (
      response: SearchBoxRetrieveResponse | CoordinateRetrieveResponse
    ) => void;
  }
) => {
  params.setInputValue(suggestion.name);
  params.setIsOpen(false);

  if (params.onChange) {
    params.onChange(suggestion.name);
  }

  if (params.onRetrieve) {
    params.onRetrieve(createCoordinateRetrieveResponse(suggestion));
  }
};

/**
 * Handles selection of a regular Mapbox suggestion (requires API retrieve).
 * Calls Mapbox retrieve() to get full details, updates UI, centers map,
 * and triggers callbacks. Also creates a new session token for billing.
 *
 * @param suggestion - The Mapbox suggestion to retrieve and select
 * @param params - Object containing refs, callbacks, and state setters
 */
export const handleRegularSelection = async (
  suggestion: SearchBoxSuggestion,
  params: {
    searchBoxCoreRef: React.MutableRefObject<SearchBoxCore | null>;
    sessionTokenRef: React.MutableRefObject<SessionToken>;
    options?: Partial<SearchBoxOptions>;
    setInputValue: (value: string) => void;
    setIsOpen: (open: boolean) => void;
    onChange?: (value: string) => void;
    onRetrieve?: (
      response: SearchBoxRetrieveResponse | CoordinateRetrieveResponse
    ) => void;
  }
) => {
  if (!params.searchBoxCoreRef.current) {
    return;
  }

  try {
    // Retrieve full details from Mapbox API
    const result = await params.searchBoxCoreRef.current.retrieve(suggestion, {
      sessionToken: params.sessionTokenRef.current,
      language: params.options?.language
    });

    params.setInputValue(suggestion.name);
    params.setIsOpen(false);

    if (params.onChange) {
      params.onChange(suggestion.name);
    }

    if (params.onRetrieve) {
      params.onRetrieve(result);
    }

    // Create new session token for next search (for billing purposes)
    params.sessionTokenRef.current = new SessionToken();
  } catch (error) {
    // Log error for debugging (no onRetrieveError callback available)
    console.error('Error retrieving suggestion:', error);
  }
};
